import React, {useCallback, useEffect, useMemo} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import Grid from '@material-ui/core/Grid';
import {useGate, useStore} from 'effector-react';
import i18next from 'i18next';
import {useForm, Controller} from 'react-hook-form';

import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';

import {validateCode} from 'api';

import {ReactComponent as ScanIcon} from 'assets/svg/scan.svg';
import {ReactComponent as TrashIcon} from 'assets/svg/trash.svg';
import {BackTo} from 'components/common/BackTo';
import {ProductGroupIcon} from 'components/common/ProductGroupIcon';
import {Button} from 'components/ui-kit/Button';
import {DatePicker} from 'components/ui-kit/DatePicker';
import {Icon} from 'components/ui-kit/Icon';
import {IconButton} from 'components/ui-kit/IconButton';
import {Input} from 'components/ui-kit/Input';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {QRScannerModal} from 'components/ui-kit/ScanCode';
import {Select, SelectOption} from 'components/ui-kit/Select';
import {DocumentReportType} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {createDocumentReturnGoodsFx} from 'models/documents/create';
import {
    CreateDocumentsReturnsGate,
    DocumentsReturnsGate,
    firstStep$,
    isFetchPending$,
    returnGoodsDictionary$,
    storages$,
} from 'models/documents/returns';
import {colors} from 'theme';
import {ALLOWED_REASONS, DocumentReturnGoodsData, GeneralInfoData, PRODUCT_CONDITIONS} from 'types';
import {CodeRequestType, CodeResponseType} from 'types/validation';
import {mapEnumToI18nOptions} from 'utils/select';
import {hasOnlyLatinLetters} from 'utils/string';

import {ALLOWANCES_BY_PRODUCT_GROUP, PRODUCT_CONDITION_OPTIONS} from './const';
import {
    H1,
    H2,
    MarkingCodeContainer,
    MarkingCodeInfo,
    MarkingCodeInfoSubtext,
    MarkingCodeInfoText,
    ProductGroupIconContainer,
    SubmitButtonContainer,
} from './styled';
import {createValidationContext, generalInfoSchema} from './validation';

export const CreateConsumerReturn = () => {
    useGate(DocumentsReturnsGate);
    useGate(CreateDocumentsReturnsGate);
    const isFetchPending = useStore(isFetchPending$);
    const [scanShow, setScanShow] = React.useState(false);
    const [pending, setPending] = React.useState(false);
    const [data, setData] = React.useState<CodeResponseType['info'] | null>(null);
    const {t} = useTranslation();
    const translate = useCallback((key: string) => t(`pages.documents.returnGoods.${key}`), [t]);
    const firstStep = useStore(firstStep$);
    const storages = useStore(storages$);
    const returnGoodsDictionary = useStore(returnGoodsDictionary$);

    const storageOptions = useMemo<SelectOption<number>[]>(
        () =>
            storages.map((storage) => ({
                value: storage.id,
                label: storage.storageName ?? '',
                isDisabled: storage.status === 'deactivated',
            })),
        [storages],
    );

    const allowances = useMemo(() => {
        if (!data?.productGroupCode) return null;
        return ALLOWANCES_BY_PRODUCT_GROUP[data.productGroupCode] ?? null;
    }, [data?.productGroupCode]);

    const reintroductionAllowed = allowances?.reintroductionAllowed ?? false;
    const formContext = useMemo(() => createValidationContext(reintroductionAllowed), [
        reintroductionAllowed,
    ]);

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: {isValid},
    } = useForm<GeneralInfoData>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(generalInfoSchema),
        context: formContext,
        defaultValues: {
            ...firstStep,
        },
    });

    const productCondition = watch('productCondition');
    const isProductRecall = !!data?.productRecall;
    const isNotFitForSale = productCondition === PRODUCT_CONDITIONS.NOT_FIT_FOR_SALE;

    const isDisposalReasonReadonly =
        productCondition === PRODUCT_CONDITIONS.FIT_FOR_SALE &&
        !reintroductionAllowed &&
        !!allowances;
    const showDisposalReason =
        productCondition === PRODUCT_CONDITIONS.NOT_FIT_FOR_SALE || isDisposalReasonReadonly;

    const autoDisposalReason = isProductRecall
        ? ALLOWED_REASONS.PRODUCT_RECALL
        : ALLOWED_REASONS.RETURN_FROM_CONSUMER;

    const disposalReasonOptions = useMemo<SelectOption<string>[]>(() => {
        if (isDisposalReasonReadonly) {
            return [
                {
                    value: autoDisposalReason,
                    label: t(`dictionaries.documents.returnGoods.reasons.${autoDisposalReason}`),
                },
            ];
        }

        const allowedReasons = allowances?.allowedReasons;
        const reasons = allowedReasons?.length
            ? returnGoodsDictionary.disposalReasons.filter((reason) =>
                  allowedReasons.includes(reason),
              )
            : returnGoodsDictionary.disposalReasons;

        return reasons.map((reason) => ({
            value: reason,
            label: t(`dictionaries.documents.returnGoods.reasons.${reason}`),
        }));
    }, [
        allowances?.allowedReasons,
        isDisposalReasonReadonly,
        autoDisposalReason,
        returnGoodsDictionary.disposalReasons,
        t,
    ]);

    useEffect(() => {
        if (!productCondition || !allowances) return;

        if (productCondition === PRODUCT_CONDITIONS.FIT_FOR_SALE) {
            if (reintroductionAllowed) {
                setValue('disposalReason', null);
            } else {
                setValue('disposalReason', autoDisposalReason);
            }
        }
    }, [productCondition, reintroductionAllowed, allowances, autoDisposalReason, setValue]);

    useEffect(() => {
        if (isNotFitForSale) {
            setValue('storageLocation', null, {shouldValidate: true});
        }
    }, [isNotFitForSale, setValue]);

    const clearMarkingCode = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setData(null);
        setValue('markingCode', '', {shouldValidate: true});
        setValue('productCondition', null);
        setValue('disposalReason', null);
        setValue('storageLocation', null);
    };

    const onHandleChanged = (formData: GeneralInfoData) => {
        const payload: DocumentReturnGoodsData = {
            markingCode: formData.markingCode,
            productName: data?.productName ?? '',
            productCode: data?.productCode ?? '',
            ...(formData.storageLocation != null && {
                storageLocation: formData.storageLocation,
            }),
            ...(formData.productCondition && {productCondition: formData.productCondition}),
            ...(formData.disposalReason && {disposalReason: formData.disposalReason}),
            ...(formData.returnDate && {returnDate: formData.returnDate.toISOString()}),
            ...(formData.referenceDocNumber && {
                referenceDocumentNumber: formData.referenceDocNumber,
            }),
            ...(formData.comment && {comment: formData.comment}),
        };

        createDocumentReturnGoodsFx(payload);
    };

    const validation = async (markingCode: string, codeInfo: CodeResponseType['info'] | null) => {
        const errors: string[] = [];

        try {
            const payload: CodeRequestType = {
                type: DocumentReportType.RETURN_GOODS,
                markingCode,
            };
            const codeData = (await validateCode(payload)).data;
            errors.push(...codeData.errors.map((e) => e.message));

            if (codeInfo && codeInfo.identityCode === codeData.info.identityCode)
                errors.push(i18next.t('components.scan.errors.double'));

            return {errors, codeData: codeData.info};
        } catch (e) {
            console.error(e);
            errors.push('Invalid code');
            return {errors, codeData: null};
        }
    };

    const onEnterCode = async (code: string) => {
        try {
            setPending(true);
            if (!hasOnlyLatinLetters(code)) {
                toast.warning(t('components.scan.layoutWarn'));
                return;
            }

            const {errors, codeData} = await validation(code, data);
            if (errors.length) {
                setData(null);
                setValue('markingCode', '', {shouldValidate: true});
                toast.error(
                    <div>
                        <strong>Errors</strong>
                        <ul style={{margin: '6px 0'}}>
                            {errors.map((e) => (
                                <li key={e}>{e}</li>
                            ))}
                        </ul>
                    </div>,
                );
                return;
            }

            if (codeData) {
                setData(codeData);
                setValue('markingCode', codeData.identityCode, {shouldValidate: true});
            }
        } catch (e) {
            console.error(e);
            toast.error((e as Error).message ?? 'Invalid code');
        } finally {
            setPending(false);
        }
    };

    return (
        <>
            <BackTo to={ROUTE.DOCUMENTS_DOCUMENTS}>{t('pages.documents.back')}</BackTo>
            <ContentContainer>
                <H1>{translate('newConsumerReturn')}</H1>
                <div>
                    <form onSubmit={handleSubmit(onHandleChanged)}>
                        <H2>{translate('markingCode')}</H2>
                        <Grid container spacing={3}>
                            <Controller
                                control={control}
                                name="markingCode"
                                render={(props, {invalid}) =>
                                    data?.identityCode ? (
                                        <Grid item sm={12}>
                                            <MarkingCodeContainer>
                                                <ProductGroupIconContainer>
                                                    {data.productGroupCode && (
                                                        <ProductGroupIcon
                                                            style={{margin: '0', padding: '0'}}
                                                            value={data.productGroupCode}
                                                        />
                                                    )}
                                                </ProductGroupIconContainer>
                                                <MarkingCodeInfo>
                                                    <MarkingCodeInfoText>
                                                        {data.identityCode}
                                                    </MarkingCodeInfoText>
                                                    <MarkingCodeInfoSubtext>
                                                        <span>
                                                            {translate('productName')}:{' '}
                                                            {data.productName}
                                                        </span>
                                                        <span>
                                                            {translate('productCode')}:{' '}
                                                            {data.productCode}
                                                        </span>
                                                    </MarkingCodeInfoSubtext>
                                                </MarkingCodeInfo>
                                                <IconButton
                                                    type="button"
                                                    onClick={clearMarkingCode}
                                                    icon={
                                                        <Icon
                                                            source={TrashIcon}
                                                            width={16}
                                                            height={16}
                                                            fillColor={colors.danger}
                                                        />
                                                    }
                                                />
                                            </MarkingCodeContainer>
                                        </Grid>
                                    ) : (
                                        <>
                                            <Grid item md={6} xs={12}>
                                                <Input
                                                    placeholder={translate(
                                                        'form.markingCodePlaceholder',
                                                    )}
                                                    label={translate('form.markingCodeLabel')}
                                                    value={props.value ?? ''}
                                                    onChange={(e) => {
                                                        onEnterCode(e.target.value);
                                                    }}
                                                    hasError={invalid}
                                                />
                                            </Grid>
                                            <Grid item md={6} xs={12}>
                                                <div
                                                    style={{
                                                        height: '100%',
                                                        display: 'flex',
                                                        alignItems: 'flex-end',
                                                    }}
                                                >
                                                    <Button
                                                        type="button"
                                                        disabled={isFetchPending || pending}
                                                        onClick={() => setScanShow(true)}
                                                    >
                                                        {t('components.scan.button')}
                                                        <Icon
                                                            style={{marginLeft: 4}}
                                                            source={ScanIcon}
                                                            width={20}
                                                            height={20}
                                                        />
                                                    </Button>
                                                </div>
                                            </Grid>
                                        </>
                                    )
                                }
                            />
                        </Grid>
                        <H2>{translate('generalInfo')}</H2>
                        <Grid container spacing={3}>
                            <Grid item md={6} xs={12}>
                                <Controller
                                    control={control}
                                    name="returnDate"
                                    render={(props, {invalid}) => (
                                        <DatePicker
                                            label={translate('form.returnDateLabel')}
                                            {...props}
                                            hasError={invalid}
                                            value={props.value}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <Controller
                                    control={control}
                                    name="referenceDocNumber"
                                    render={(props, {invalid}) => (
                                        <Input
                                            placeholder={translate(
                                                'form.referenceDocNumberPlaceholder',
                                            )}
                                            label={translate('form.referenceDocNumberLabel')}
                                            {...props}
                                            hasError={invalid}
                                            value={props.value}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <Controller
                                    control={control}
                                    name="productCondition"
                                    render={(props, {invalid}) => (
                                        <Select
                                            isRequired
                                            placeholder={translate(
                                                'form.productConditionPlaceholder',
                                            )}
                                            label={translate('form.productConditionLabel')}
                                            hasError={invalid}
                                            options={mapEnumToI18nOptions(
                                                PRODUCT_CONDITION_OPTIONS,
                                                'dictionaries.documents.returnGoods.productCondition',
                                            )}
                                            value={
                                                mapEnumToI18nOptions(
                                                    PRODUCT_CONDITION_OPTIONS,
                                                    'dictionaries.documents.returnGoods.productCondition',
                                                ).find((option) => option.value === props.value) ??
                                                null
                                            }
                                            onChange={(val: SelectOption<string>) => {
                                                props.onChange(val?.value ?? '');
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            {!isNotFitForSale && (
                                <Grid item md={6} xs={12}>
                                    <Controller
                                        control={control}
                                        name="storageLocation"
                                        render={(props, {invalid}) => (
                                            <Select
                                                isRequired
                                                placeholder={translate(
                                                    'form.storageLocationPlaceholder',
                                                )}
                                                label={translate('form.storageLocationLabel')}
                                                hasError={invalid}
                                                options={storageOptions}
                                                value={
                                                    storageOptions.find(
                                                        (option) => option.value === props.value,
                                                    ) ?? null
                                                }
                                                onChange={(val: SelectOption<number>) => {
                                                    props.onChange(val?.value ?? null);
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                            )}
                            {showDisposalReason && (
                                <Grid item md={6} xs={12}>
                                    <Controller
                                        control={control}
                                        name="disposalReason"
                                        render={(props, {invalid}) => (
                                            <Select
                                                isRequired
                                                placeholder={translate(
                                                    'form.disposalReasonPlaceholder',
                                                )}
                                                label={translate('form.disposalReasonLabel')}
                                                hasError={invalid}
                                                disabled={isDisposalReasonReadonly}
                                                options={disposalReasonOptions}
                                                value={
                                                    disposalReasonOptions.find(
                                                        (option) => option.value === props.value,
                                                    ) ?? null
                                                }
                                                onChange={(val: SelectOption<string>) => {
                                                    if (isDisposalReasonReadonly) return;
                                                    props.onChange(val?.value ?? '');
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                            )}
                            <Grid item md={6} xs={12}>
                                <Controller
                                    control={control}
                                    name="comment"
                                    render={(props, {invalid}) => (
                                        <Input
                                            placeholder={translate('form.commentPlaceholder')}
                                            label={translate('form.commentLabel')}
                                            {...props}
                                            hasError={invalid}
                                            value={props.value}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                        <SubmitButtonContainer>
                            <Button
                                disabled={pending || isFetchPending || !isValid}
                                style={{width: 'auto'}}
                                type="submit"
                            >
                                {translate('form.submit')}
                            </Button>
                        </SubmitButtonContainer>
                    </form>
                    <QRScannerModal
                        open={scanShow}
                        pending={pending}
                        onClose={() => setScanShow(false)}
                        onScan={onEnterCode}
                        onManual={onEnterCode}
                    />
                </div>
            </ContentContainer>
        </>
    );
};
